import { IconProps, Loading } from '@velocity-exchange/icons';
import { ComponentType } from 'react';
import { cj, cm } from 'src/utils/classNames';
import { WithChildren, WithClassName, WithRef } from 'src/utils/UtilityTypes';

interface BaseProps
	extends
		WithChildren,
		WithClassName,
		React.ButtonHTMLAttributes<HTMLButtonElement> {
	/**
	 * Defaults to primary.
	 */
	variant?: 'primary' | 'secondary' | 'tertiary';
	/**
	 * Defaults to md.
	 */
	size?: 'sm' | 'md' | 'lg';
	isLoading?: boolean;
	dataTestId?: string;
}

interface WithLeftIconProps {
	leftIcon?: ComponentType<IconProps>;
	rightIcon?: never;
}

interface WithRightIconProps {
	leftIcon?: never;
	rightIcon?: ComponentType<IconProps>;
}

type Props = BaseProps & (WithLeftIconProps | WithRightIconProps);

export default function TextButton({
	variant = 'primary',
	size = 'md',
	className,
	children,
	leftIcon: LeftIcon,
	rightIcon: RightIcon,
	isLoading,
	disabled,
	dataTestId,
	...rest
}: WithRef<Props, HTMLButtonElement>) {
	const variantClassName = {
		primary: cj(
			'text-text-default hover:text-text-secondary disabled:text-text-disabled'
		),
		secondary: cj(
			'text-text-secondary hover:text-text-default disabled:text-text-disabled'
		),
		tertiary: cj(
			'text-purple-link hover:text-purple-link-hover disabled:text-text-disabled'
		),
	}[variant];

	const sizeClassName = {
		lg: 'typo-label-md',
		md: 'typo-label-sm',
		sm: 'typo-label-xs',
	}[size];

	const iconSize = size === 'sm' ? 14 : 16;

	return (
		<button
			className={cm(
				'inline-flex items-center justify-center gap-x-xs whitespace-nowrap',
				variantClassName,
				sizeClassName,
				className
			)}
			disabled={isLoading || disabled}
			data-testid={dataTestId}
			{...rest}
		>
			{/* `shrink-0` on icons to ensure they don't shrink in WebKit. */}
			{LeftIcon && !isLoading && (
				<LeftIcon className="shrink-0" size={iconSize} />
			)}
			{isLoading && <Loading className="animate-spin" size={iconSize} />}
			{children}
			{RightIcon && <RightIcon className="shrink-0" size={iconSize} />}
		</button>
	);
}
